import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import { useUserSettings } from "./useUserSettings";
import type { Task, GetTasksResponse } from "../types";
import { THIS_SESSION_GROUP_SLUG, TODO_GROUP_SLUG } from "../types";
export function useSimplifiableTasks() {
  const $ = _c(17);
  const apiFetcher = useApiFetcher();
  const {
    data: userSettings
  } = useUserSettings();
  const threshold = userSettings?.taskSimplificationThreshold ?? 45;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [...QUERY_KEYS.tasks.infinite(THIS_SESSION_GROUP_SLUG), "simplifiable"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== apiFetcher) {
    t1 = {
      queryKey: t0,
      queryFn: () => apiFetcher(`/api/v1/tasks?page=1&limit=10&groupSlug=${THIS_SESSION_GROUP_SLUG}`),
      staleTime: 60000,
      gcTime: 300000
    };
    $[1] = apiFetcher;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: thisSessionData,
    isLoading: isLoadingThisSession
  } = useQuery(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...QUERY_KEYS.tasks.infinite(TODO_GROUP_SLUG), "simplifiable"];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== apiFetcher) {
    t3 = {
      queryKey: t2,
      queryFn: () => apiFetcher(`/api/v1/tasks?page=1&limit=5&groupSlug=${TODO_GROUP_SLUG}`),
      staleTime: 60000,
      gcTime: 300000
    };
    $[4] = apiFetcher;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: todoData,
    isLoading: isLoadingTodo
  } = useQuery(t3);
  const isLoading = isLoadingThisSession || isLoadingTodo;
  let t4;
  bb0: {
    if (isLoading || !thisSessionData || !todoData) {
      let t5;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t4 = t5;
      break bb0;
    }
    let combined;
    if ($[7] !== thisSessionData.data || $[8] !== threshold || $[9] !== todoData.data) {
      const thisSessionTasks = (thisSessionData.data || []) as Task[];
      const todoTasks = (todoData.data || []) as Task[];
      let t5;
      if ($[11] !== threshold) {
        t5 = task => (task.estimatedTime ?? 0) >= threshold && !task.completedAt;
        $[11] = threshold;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      const filterTask = t5;
      combined = [];
      const filteredThisSession = thisSessionTasks.filter(filterTask);
      combined.push(...filteredThisSession.slice(0, 5).map(_temp));
      const remaining = 5 - combined.length;
      if (remaining > 0) {
        const filteredTodo = todoTasks.filter(filterTask);
        combined.push(...filteredTodo.slice(0, Math.min(remaining, 3)).map(_temp2));
      }
      $[7] = thisSessionData.data;
      $[8] = threshold;
      $[9] = todoData.data;
      $[10] = combined;
    } else {
      combined = $[10];
    }
    t4 = combined;
  }
  const processedTasks = t4;
  let t5;
  if ($[13] !== isLoading || $[14] !== processedTasks || $[15] !== threshold) {
    t5 = {
      data: processedTasks,
      isLoading,
      error: null,
      threshold
    };
    $[13] = isLoading;
    $[14] = processedTasks;
    $[15] = threshold;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp2(t_0) {
  return {
    ...t_0,
    _sourceGroup: TODO_GROUP_SLUG
  };
}
function _temp(t) {
  return {
    ...t,
    _sourceGroup: THIS_SESSION_GROUP_SLUG
  };
}